import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { getDigestPreferences, saveDigestPreferences } from '../../services/emailNotificationService';

const NOTIFICATION_TYPES = {
  appointment_confirmation: {
    label: 'Confirmation RDV',
    icon: 'CalendarCheck',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    border: 'border-l-blue-400',
  },
  property_inquiry: {
    label: 'Demande Propriété',
    icon: 'Home',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    border: 'border-l-amber-400',
  },
  notary_appointment: {
    label: 'RDV Notaire',
    icon: 'Scale',
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
    border: 'border-l-purple-400',
  },
  appointment: {
    label: 'Rendez-vous',
    icon: 'Calendar',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    border: 'border-l-blue-400',
  },
  property_update: {
    label: 'Mise à jour',
    icon: 'RefreshCw',
    color: 'bg-green-50 text-green-700 border-green-200',
    dot: 'bg-green-500',
    border: 'border-l-green-400',
  },
  document_ready: {
    label: 'Document prêt',
    icon: 'FileText',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    border: 'border-l-indigo-400',
  },
  general: {
    label: 'Général',
    icon: 'Bell',
    color: 'bg-gray-50 text-gray-700 border-gray-200',
    dot: 'bg-gray-400',
    border: 'border-l-gray-300',
  },
};

const getTypeConfig = (type) =>
  NOTIFICATION_TYPES?.[type] || NOTIFICATION_TYPES?.general;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immédiat', description: 'Envoi dès la réception', icon: 'Zap' },
  { value: 'daily', label: 'Résumé quotidien', description: 'Un email groupé chaque matin', icon: 'Sun' },
  { value: 'weekly', label: 'Résumé hebdomadaire', description: 'Un email groupé chaque lundi', icon: 'CalendarDays' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Dimanche' },
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
];

const DigestPreferencesPanel = ({ user }) => {
  const [prefs, setPrefs] = useState({
    frequency: 'immediate',
    appointmentFrequency: null,
    inquiryFrequency: null,
    notaryFrequency: null,
    preferredHour: 8,
    preferredDayOfWeek: 1,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      const result = await getDigestPreferences(user?.id, user?.email);
      if (result?.success && result?.preferences) {
        setPrefs(result?.preferences);
      }
      setLoading(false);
    })();
  }, [user?.id]);

  const handleSave = async () => {
    setSaving(true);
    const result = await saveDigestPreferences(user?.id, user?.email, prefs);
    setSaving(false);
    if (result?.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const FrequencySelector = ({ label, value, onChange, icon }) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center space-x-1.5 mb-0.5">
        <Icon name={icon} size={13} className="text-text-secondary" />
        <span className="text-ios-caption1 font-body-medium text-text-secondary">{label}</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {FREQUENCY_OPTIONS?.map((opt) => (
          <button
            key={opt?.value}
            onClick={() => onChange(opt?.value === value ? null : opt?.value)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-ios-caption1 font-system border transition-all duration-150 ${
              value === opt?.value
                ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
                : 'bg-surface text-text-secondary border-border hover:border-brand-earth/40 hover:text-text-primary'
            }`}
          >
            <Icon name={opt?.icon} size={12} />
            <span>{opt?.label}</span>
          </button>
        ))}
        {value !== null && (
          <button
            onClick={() => onChange(null)}
            className="px-2.5 py-1.5 rounded-xl text-ios-caption1 text-text-secondary border border-border hover:border-red-300 hover:text-red-500 transition-all duration-150"
            title="Utiliser le paramètre global"
          >
            <Icon name="RotateCcw" size={12} />
          </button>
        )}
      </div>
      {value === null && (
        <p className="text-ios-caption2 text-text-secondary italic">Utilise le paramètre global</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-5 mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          <span className="text-ios-callout text-text-secondary">Chargement des préférences…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 mb-6 overflow-hidden">
      {/* Header toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-surface/50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center">
            <Icon name="Mail" size={18} className="text-brand-earth" />
          </div>
          <div className="text-left">
            <p className="text-ios-callout font-system font-body-semibold text-text-primary">
              Préférences de résumé email
            </p>
            <p className="text-ios-caption1 text-text-secondary mt-0.5">
              {prefs?.frequency === 'immediate' ? 'Envoi immédiat activé' :
               prefs?.frequency === 'daily'? 'Résumé quotidien activé' : 'Résumé hebdomadaire activé'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {saved && (
            <span className="flex items-center space-x-1 text-ios-caption1 text-green-600 bg-green-50 px-2.5 py-1 rounded-lg">
              <Icon name="CheckCircle" size={12} />
              <span>Sauvegardé</span>
            </span>
          )}
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-text-secondary" />
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-border/50 pt-5 space-y-6">
          {/* Global frequency */}
          <div>
            <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">
              Fréquence globale
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FREQUENCY_OPTIONS?.map((opt) => (
                <button
                  key={opt?.value}
                  onClick={() => setPrefs((p) => ({ ...p, frequency: opt?.value }))}
                  className={`flex flex-col items-start p-4 rounded-xl border transition-all duration-150 text-left ${
                    prefs?.frequency === opt?.value
                      ? 'bg-brand-earth/5 border-brand-earth text-brand-earth ring-1 ring-brand-earth/20' :'bg-surface border-border hover:border-brand-earth/40'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                    prefs?.frequency === opt?.value ? 'bg-brand-earth/15' : 'bg-white border border-border'
                  }`}>
                    <Icon name={opt?.icon} size={16} className={prefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
                  </div>
                  <p className={`text-ios-callout font-body-semibold ${prefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-primary'}`}>
                    {opt?.label}
                  </p>
                  <p className="text-ios-caption1 text-text-secondary mt-0.5">{opt?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Per-type overrides */}
          <div>
            <p className="text-ios-footnote font-body-semibold text-text-primary mb-1">
              Remplacements par type
            </p>
            <p className="text-ios-caption1 text-text-secondary mb-4">
              Personnalisez la fréquence pour chaque type de notification. Laissez vide pour utiliser le paramètre global.
            </p>
            <div className="space-y-4 bg-surface rounded-xl p-4 border border-border/50">
              <FrequencySelector
                label="Confirmations de rendez-vous"
                icon="CalendarCheck"
                value={prefs?.appointmentFrequency}
                onChange={(v) => setPrefs((p) => ({ ...p, appointmentFrequency: v }))}
              />
              <div className="border-t border-border/50" />
              <FrequencySelector
                label="Demandes de propriété"
                icon="Home"
                value={prefs?.inquiryFrequency}
                onChange={(v) => setPrefs((p) => ({ ...p, inquiryFrequency: v }))}
              />
              <div className="border-t border-border/50" />
              <FrequencySelector
                label="Rendez-vous notaire"
                icon="Scale"
                value={prefs?.notaryFrequency}
                onChange={(v) => setPrefs((p) => ({ ...p, notaryFrequency: v }))}
              />
            </div>
          </div>

          {/* Delivery time (shown only when daily or weekly is selected) */}
          {(prefs?.frequency === 'daily' || prefs?.frequency === 'weekly' ||
            prefs?.appointmentFrequency === 'daily' || prefs?.appointmentFrequency === 'weekly' ||
            prefs?.inquiryFrequency === 'daily' || prefs?.inquiryFrequency === 'weekly' ||
            prefs?.notaryFrequency === 'daily' || prefs?.notaryFrequency === 'weekly') && (
            <div>
              <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">
                Heure de livraison préférée
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                  <Icon name="Clock" size={15} className="text-text-secondary flex-shrink-0" />
                  <select
                    value={prefs?.preferredHour}
                    onChange={(e) => setPrefs((p) => ({ ...p, preferredHour: parseInt(e?.target?.value) }))}
                    className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i)?.padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
                {prefs?.frequency === 'weekly' && (
                  <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                    <Icon name="CalendarDays" size={15} className="text-text-secondary flex-shrink-0" />
                    <select
                      value={prefs?.preferredDayOfWeek}
                      onChange={(e) => setPrefs((p) => ({ ...p, preferredDayOfWeek: parseInt(e?.target?.value) }))}
                      className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                    >
                      {DAYS_OF_WEEK?.map((d) => (
                        <option key={d?.value} value={d?.value}>{d?.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Save button */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sauvegarde…</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} color="white" />
                  <span>Sauvegarder les préférences</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationHistory = () => {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0 });

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase?.from('notifications')?.select('*');

      if (user?.id) {
        query = query?.eq('recipient_id', user?.id);
      }

      if (filterType !== 'all') {
        query = query?.eq('type', filterType);
      }

      if (filterStatus === 'unread') {
        query = query?.eq('is_read', false);
      } else if (filterStatus === 'read') {
        query = query?.eq('is_read', true);
      }

      if (filterDateRange !== 'all') {
        const now = new Date();
        let from;
        if (filterDateRange === '7d') from = new Date(now - 7 * 86400000);
        else if (filterDateRange === '30d') from = new Date(now - 30 * 86400000);
        else if (filterDateRange === '90d') from = new Date(now - 90 * 86400000);
        if (from) query = query?.gte('created_at', from.toISOString());
      }

      const order = sortOrder === 'oldest' ? { ascending: true } : { ascending: false };
      const { data, error } = await query?.order('created_at', order);

      if (error) throw error;

      const all = data || [];
      setNotifications(all);
      setStats({
        total: all?.length,
        unread: all?.filter((n) => !n?.is_read)?.length,
        read: all?.filter((n) => n?.is_read)?.length,
      });
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filterType, filterStatus, filterDateRange, sortOrder]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
      setNotifications((prev) =>
        prev?.map((n) => (n?.id === id ? { ...n, is_read: true } : n))
      );
      setStats((prev) => ({ ...prev, unread: Math.max(0, prev?.unread - 1), read: prev?.read + 1 }));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      let query = supabase?.from('notifications')?.update({ is_read: true })?.eq('is_read', false);
      if (user?.id) query = query?.eq('recipient_id', user?.id);
      await query;
      setNotifications((prev) => prev?.map((n) => ({ ...n, is_read: true })));
      setStats((prev) => ({ ...prev, unread: 0, read: prev?.total }));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette notification ?')) return;
    try {
      await supabase?.from('notifications')?.delete()?.eq('id', id);
      setNotifications((prev) => prev?.filter((n) => n?.id !== id));
      setStats((prev) => ({ ...prev, total: prev?.total - 1 }));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const filtered = notifications?.filter((n) => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (
      n?.title?.toLowerCase()?.includes(q) ||
      n?.message?.toLowerCase()?.includes(q) ||
      n?.type?.toLowerCase()?.includes(q)
    );
  });

  const typeOptions = [
    { value: 'all', label: 'Tous les types' },
    { value: 'appointment_confirmation', label: 'Confirmation RDV' },
    { value: 'property_inquiry', label: 'Demande Propriété' },
    { value: 'notary_appointment', label: 'RDV Notaire' },
    { value: 'appointment', label: 'Rendez-vous' },
    { value: 'property_update', label: 'Mise à jour' },
    { value: 'document_ready', label: 'Document prêt' },
    { value: 'general', label: 'Général' },
  ];

  return (
    <>
      <Helmet>
        <title>Historique des Notifications – Kapital States</title>
        <meta name="description" content="Consultez l'historique complet de vos notifications : confirmations de rendez-vous, demandes de propriétés et mises à jour notariales." />
      </Helmet>

      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary">Notifications</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth leading-tight">
                  Historique des Notifications
                </h1>
                <p className="text-ios-callout text-text-secondary mt-1">
                  Confirmations de RDV, demandes de propriétés et mises à jour notariales
                </p>
              </div>
              <div className="flex items-center space-x-3 self-start sm:self-auto">
                <Link
                  to="/notification-preferences"
                  className="flex items-center space-x-2 px-4 py-2.5 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium hover:border-brand-earth/40 hover:text-brand-earth transition-all duration-200 shadow-ios-light"
                >
                  <Icon name="Settings2" size={16} />
                  <span>Préférences</span>
                </Link>
                {stats?.unread > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center space-x-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-medium hover:bg-brand-earth/90 transition-all duration-200 shadow-ios-light"
                  >
                    <Icon name="CheckCheck" size={16} color="white" />
                    <span>Tout marquer lu</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { label: 'Total', value: stats?.total, icon: 'Bell', color: 'text-brand-earth', bg: 'bg-brand-earth/10' },
              { label: 'Non lues', value: stats?.unread, icon: 'BellDot', color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Lues', value: stats?.read, icon: 'BellOff', color: 'text-green-600', bg: 'bg-green-50' },
            ]?.map((s) => (
              <div key={s?.label} className="bg-white rounded-2xl p-4 shadow-ios-light border border-border/50 flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-xl ${s?.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={s?.icon} size={20} className={s?.color} />
                </div>
                <div>
                  <p className="text-ios-title2 font-display font-display-semibold text-text-primary leading-none">{s?.value}</p>
                  <p className="text-ios-caption1 text-text-secondary mt-0.5">{s?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Digest Preferences Panel */}
          {user && <DigestPreferencesPanel user={user} />}

          {/* Search & Filters */}
          <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-5 mb-6">
            {/* Search */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
                <Icon name="Search" size={17} className="text-text-secondary" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Rechercher dans les notifications..."
                className="w-full pl-10 pr-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-3.5 flex items-center text-text-secondary hover:text-text-primary transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3">
              {/* Type */}
              <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                <Icon name="Tag" size={15} className="text-text-secondary flex-shrink-0" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                >
                  {typeOptions?.map((o) => (
                    <option key={o?.value} value={o?.value}>{o?.label}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="flex items-center space-x-2 flex-1 min-w-[140px]">
                <Icon name="CircleDot" size={15} className="text-text-secondary flex-shrink-0" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="unread">Non lues</option>
                  <option value="read">Lues</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="flex items-center space-x-2 flex-1 min-w-[140px]">
                <Icon name="CalendarDays" size={15} className="text-text-secondary flex-shrink-0" />
                <select
                  value={filterDateRange}
                  onChange={(e) => setFilterDateRange(e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                >
                  <option value="all">Toute période</option>
                  <option value="7d">7 derniers jours</option>
                  <option value="30d">30 derniers jours</option>
                  <option value="90d">90 derniers jours</option>
                </select>
              </div>

              {/* Sort */}
              <div className="flex items-center space-x-2 flex-1 min-w-[140px]">
                <Icon name="ArrowUpDown" size={15} className="text-text-secondary flex-shrink-0" />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                >
                  <option value="newest">Plus récentes</option>
                  <option value="oldest">Plus anciennes</option>
                </select>
              </div>
            </div>

            {/* Active filters summary */}
            {(filterType !== 'all' || filterStatus !== 'all' || filterDateRange !== 'all' || searchQuery) && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                <p className="text-ios-footnote text-text-secondary">
                  <span className="font-body-medium text-text-primary">{filtered?.length}</span> résultat{filtered?.length !== 1 ? 's' : ''} trouvé{filtered?.length !== 1 ? 's' : ''}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                    setFilterStatus('all');
                    setFilterDateRange('all');
                    setSortOrder('newest');
                  }}
                  className="text-ios-footnote text-brand-earth font-body-medium hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="space-y-3">
            {loading ? (
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-12 text-center">
                <div className="w-10 h-10 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-ios-callout text-text-secondary">Chargement des notifications…</p>
              </div>
            ) : filtered?.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-16 text-center">
                <div className="w-16 h-16 bg-surface rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="BellOff" size={32} className="text-text-secondary" />
                </div>
                <h3 className="text-ios-title3 font-display font-display-semibold text-text-primary mb-2">
                  Aucune notification
                </h3>
                <p className="text-ios-callout text-text-secondary max-w-xs mx-auto">
                  {searchQuery || filterType !== 'all' || filterStatus !== 'all' ? 'Aucun résultat pour ces critères. Essayez de modifier vos filtres.' : 'Vous n\'avez pas encore de notifications. Elles apparaîtront ici après vos actions.'}
                </p>
              </div>
            ) : (
              filtered?.map((notification) => {
                const cfg = getTypeConfig(notification?.type);
                return (
                  <div
                    key={notification?.id}
                    className={`bg-white rounded-2xl shadow-ios-light border border-border/50 border-l-4 ${cfg?.border} transition-all duration-200 hover:shadow-ios-medium ${!notification?.is_read ? 'ring-1 ring-brand-gold/20' : ''}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg?.color} border`}>
                          <Icon name={cfg?.icon} size={18} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className={`text-ios-callout font-system font-body-semibold leading-snug ${notification?.is_read ? 'text-text-secondary' : 'text-text-primary'}`}>
                              {notification?.title || 'Notification'}
                            </h3>
                            {!notification?.is_read && (
                              <span className="inline-block w-2 h-2 rounded-full bg-brand-gold flex-shrink-0" />
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-ios-caption1 font-body-medium border ${cfg?.color}`}>
                              {cfg?.label}
                            </span>
                          </div>

                          <p className={`text-ios-footnote leading-relaxed ${notification?.is_read ? 'text-text-secondary' : 'text-text-primary'}`}>
                            {notification?.message || '—'}
                          </p>

                          <div className="flex flex-wrap items-center gap-3 mt-2.5">
                            <span className="flex items-center space-x-1 text-ios-caption1 text-text-secondary">
                              <Icon name="Clock" size={12} />
                              <span>{formatDate(notification?.created_at)}</span>
                            </span>
                            {notification?.entity_type && (
                              <span className="flex items-center space-x-1 text-ios-caption1 text-text-secondary">
                                <Icon name="Hash" size={12} />
                                <span className="font-mono bg-surface px-1.5 py-0.5 rounded-md">
                                  {notification?.entity_type}
                                </span>
                              </span>
                            )}
                            {notification?.is_read && (
                              <span className="flex items-center space-x-1 text-ios-caption1 text-green-600">
                                <Icon name="CheckCircle" size={12} />
                                <span>Lu</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notification?.is_read && (
                            <button
                              onClick={() => handleMarkAsRead(notification?.id)}
                              title="Marquer comme lu"
                              className="w-8 h-8 rounded-xl bg-surface hover:bg-brand-earth/10 flex items-center justify-center transition-colors group"
                            >
                              <Icon name="Check" size={15} className="text-text-secondary group-hover:text-brand-earth" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notification?.id)}
                            title="Supprimer"
                            className="w-8 h-8 rounded-xl bg-surface hover:bg-red-50 flex items-center justify-center transition-colors group"
                          >
                            <Icon name="Trash2" size={15} className="text-text-secondary group-hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer count */}
          {!loading && filtered?.length > 0 && (
            <p className="text-center text-ios-footnote text-text-secondary mt-8">
              Affichage de <span className="font-body-medium text-text-primary">{filtered?.length}</span> notification{filtered?.length !== 1 ? 's' : ''}
            </p>
          )}
        </main>
      </div>
    </>
  );
};

export default NotificationHistory;
